import { Colors } from "@/enums/Colors";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import ExpandLess from "@mui/icons-material/ExpandLess";
import ExpandMore from "@mui/icons-material/ExpandMore";
import {
  Box,
  Collapse,
  Drawer,
  IconButton,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Tooltip,
  Typography,
  useTheme,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import React, { useCallback, useState } from "react";
import { NavLink, useLocation } from "react-router-dom";
import { LegacyLanguageSwitch } from "../common/LegacyLanguageSwitch";
import { LazoLogo } from "../drawer-components/LazoLogo";

const ExpandToggle = styled(Box)({
  display: "flex",
  alignItems: "center",
  cursor: "pointer",
  padding: "4px",
});

interface SidebarItem {
  key: string;
  label: string;
  icon?: React.ReactNode;
  path?: string;
  href?: string;
  items?: SidebarItem[];
  title?: string;
  // Renders the entry with `display: none` instead of removing it from the array, so the menu
  // structure stays stable across visibility changes and the item can be revealed via CSS.
  hidden?: boolean;
  // Runs on plain left-click instead of the native anchor. Modifier-clicks (cmd/ctrl/shift,
  // middle-click) bypass it so "open in new tab" keeps native anchor semantics. Used by
  // entries that need an async step (e.g. minting a cross-domain OTC) before navigating.
  onCustomClick?: () => Promise<void>;
}

interface SidebarProps {
  open: boolean;
  onClose: () => void;
  onToggle: () => void;
  width: number;
  mobileOpen: boolean;
  onMobileClose: () => void;
  items?: any[];
  footer?: React.ReactNode | ((open: boolean) => React.ReactNode);
  header?: React.ReactNode | ((open: boolean) => React.ReactNode);
}

export function Sidebar({ open, onClose, onToggle, width, mobileOpen, onMobileClose, items = [], footer, header }: SidebarProps) {
  const theme = useTheme();

  const drawerContent = (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        height: "100%",
      }}
    >
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-around",
          padding: theme.spacing(0, 1),
          height: "64px",
          flexShrink: 0,
        }}
      >
        {open && <LazoLogo imgStyle={{ filter: "brightness(100)" }} imageWidth={"104px"} containerSxProps={{}} />}

        <IconButton
          onClick={onToggle}
          sx={{
            color: "white",
            position: "static",
            right: "auto",
          }}
        >
          {open ? <ChevronLeftIcon /> : <ChevronRightIcon />}
        </IconButton>
      </Box>

      {/* Custom Header Section (Below Logo) */}
      {header &&
        (typeof header === "function" ? (
          <Box sx={{ px: open ? 2 : 0, pb: open ? 1 : 0, flexShrink: 0 }}>{header(open)}</Box>
        ) : (
          open && <Box sx={{ px: 2, pb: 1 }}>{header}</Box>
        ))}

      <Box sx={{ flexGrow: 1, overflowY: "auto", overflowX: "hidden" }}>
        <List disablePadding>
          {items.map((item, index) => (
            <SidebarItemRenderer key={item.key || index} item={item} open={open} />
          ))}
        </List>
      </Box>

      {/* Footer Section */}
      {footer && <Box sx={{ p: 1 }}>{typeof footer === "function" ? footer(open) : footer}</Box>}
    </Box>
  );

  return (
    <Box component="nav" sx={{ width: { sm: open ? width : `calc(${theme.spacing(7)} + 1px)` }, flexShrink: { sm: 0 } }}>
      {/* Mobile Drawer */}
      <Drawer
        variant="temporary"
        open={mobileOpen}
        onClose={onMobileClose}
        ModalProps={{
          keepMounted: true,
        }}
        sx={{
          display: { xs: "block", sm: "none" },
          "& .MuiDrawer-paper": {
            boxSizing: "border-box",
            width: width,
            backgroundColor: "black",
            color: "white",
          },
        }}
      >
        {/* Mobile header */}
        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", p: 1, height: "64px" }}>
          <LazoLogo imgStyle={{ filter: "brightness(100)" }} imageWidth={"104px"} containerSxProps={{}} />
          <LegacyLanguageSwitch />
        </Box>

        {/* Custom Header Section (below Logo) — shared with desktop */}
        {header && <Box sx={{ px: 2, pb: 1, flexShrink: 0 }}>{typeof header === "function" ? header(true) : header}</Box>}

        {/* Mobile Content (same list) */}
        <Box sx={{ flexGrow: 1, overflowY: "auto", overflowX: "hidden" }}>
          <List disablePadding>
            {items.map((item, index) => (
              <SidebarItemRenderer key={item.key || index} item={item} open={true} />
            ))}
          </List>
        </Box>

        {/* Mobile Footer Section */}
        {footer && <Box sx={{ p: 1 }}>{typeof footer === "function" ? footer(true) : footer}</Box>}
      </Drawer>

      {/* Desktop Permanent Drawer */}
      <Drawer
        variant="permanent"
        open={open}
        sx={{
          display: { xs: "none", sm: "block" },
          width: open ? width : `calc(${theme.spacing(7)} + 1px)`,
          flexShrink: 0,
          whiteSpace: "nowrap",
          boxSizing: "border-box",
          "& .MuiDrawer-paper": {
            width: open ? width : `calc(${theme.spacing(7)} + 1px)`,
            transition: theme.transitions.create("width", {
              easing: theme.transitions.easing.sharp,
              duration: theme.transitions.duration.enteringScreen,
            }),
            overflowX: "hidden",
            backgroundColor: "black",
            color: "white",
          },
        }}
      >
        {drawerContent}
      </Drawer>
    </Box>
  );
}

function SidebarItemRenderer({ item, open }: { item: SidebarItem; open: boolean }) {
  const childItems = item.items;
  const hasChildren = childItems && childItems.length > 0;
  const location = useLocation();

  // Auto-expand when the parent or a child route is active
  const isChildActive = hasChildren && childItems.some((child: SidebarItem) => child.path && location.pathname === child.path);
  const isSelfActive = hasChildren && !!item.path && location.pathname === item.path;
  const [expanded, setExpanded] = useState(isChildActive || isSelfActive);

  const handleExpand = useCallback(
    (e: React.MouseEvent) => {
      e.preventDefault();
      e.stopPropagation();
      if (open) setExpanded((prev) => !prev);
    },
    [open]
  );

  const isActive = item.path ? location.pathname === item.path : false;

  if (item.title && !item.icon && !item.path && !item.key) {
    if (!open) return null;
    return (
      <Typography
        variant="caption"
        sx={{
          color: Colors.LIGHT_GRAY_BFC2C6,
          px: 3,
          py: 1,
          mt: 1,
          display: "block",
          fontWeight: 400,
          textTransform: "uppercase",
        }}
      >
        {item.title}
      </Typography>
    );
  }

  if (!item.label && !item.title) return null;

  const label = item.label || item.title;

  const iconColor = isActive || isChildActive ? Colors.LAZO_PURPLE : "white";

  return (
    <>
      <ListItem disablePadding sx={{ display: item.hidden ? "none" : "block" }}>
        <Tooltip title={!open ? label : ""} placement="right" arrow>
          <ListItemButton
            // A parent with children is a pure expand/collapse toggle — never a navigating
            // anchor/NavLink — so clicking it doesn't fire a redirect (e.g. to the Finance
            // dashboard) while also opening its sub-menu.
            component={hasChildren ? "div" : item.path ? NavLink : item.href ? "a" : "div"}
            to={hasChildren ? undefined : item.path || undefined}
            href={hasChildren ? undefined : item.href || undefined}
            onClick={
              hasChildren
                ? handleExpand
                : item.onCustomClick
                  ? async (e: React.MouseEvent<HTMLAnchorElement>) => {
                      // Modifier-clicks fall through to the native anchor so "open in new tab" works.
                      if (e.defaultPrevented || e.metaKey || e.ctrlKey || e.shiftKey || e.button !== 0) return;
                      e.preventDefault();
                      await item.onCustomClick!();
                    }
                  : undefined
            }
            sx={{
              minHeight: 40,
              justifyContent: open ? "initial" : "center",
              borderRadius: open ? "0.5rem" : 0,
              my: 0.5,
            }}
          >
            <ListItemIcon
              sx={{
                minWidth: 0,
                mr: open ? 3 : "auto",
                justifyContent: "center",
                color: iconColor,
                "& svg": { width: 18, height: 18 },
                "& img": { width: 18, height: 18 },
              }}
            >
              {item.icon ? item.icon : hasChildren ? <div style={{ width: 24 }} /> : null}
            </ListItemIcon>
            <ListItemText primary={label} sx={{ opacity: open ? 1 : 0 }} />
            {open && hasChildren ? (
              <ExpandToggle component="span" onClick={handleExpand}>
                {expanded ? <ExpandLess /> : <ExpandMore />}
              </ExpandToggle>
            ) : null}
          </ListItemButton>
        </Tooltip>
      </ListItem>
      {hasChildren && open && (
        <Collapse in={expanded} timeout="auto" unmountOnExit>
          <List component="div" disablePadding>
            {childItems.map((subItem: SidebarItem, idx: number) => (
              <SidebarItemRenderer key={subItem.key || idx} item={subItem} open={open} />
            ))}
          </List>
        </Collapse>
      )}
    </>
  );
}
